import React from 'react';
import { 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardActionArea,
  Box,
  IconButton,
  alpha,
  useTheme,
  Chip,
  Paper,
  Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useFavorites } from '../hooks/useFavorites';
import FavoriteIcon from '@mui/icons-material/Favorite';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import { useThemeMode } from '../App';

const Favorites: React.FC = () => {
  const navigate = useNavigate();
  const { favorites, removeFromFavorites } = useFavorites();
  const theme = useTheme();
  const { mode } = useThemeMode();

  if (favorites.length === 0) {
    return (
      <Container className="fade-in" sx={{ py: 3 }}>
        <Paper
          elevation={0}
          sx={{
            p: 5,
            borderRadius: 3,
            textAlign: 'center',
            backgroundColor: alpha(theme.palette.background.paper, mode === 'dark' ? 0.6 : 0.8),
            border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          }}
        >
          <BookmarkIcon sx={{ fontSize: 60, color: alpha(theme.palette.primary.main, 0.5), mb: 2 }} />
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
            Избранные статьи
          </Typography>
          <Typography variant="body1" color="text.secondary">
            У вас пока нет избранных статей. Добавляйте полезные материалы в избранное, 
            чтобы быстро находить их в будущем.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container className="fade-in" sx={{ py: 3 }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
        <BookmarkIcon color="primary" sx={{ fontSize: 30 }} />
        <Typography variant="h4" component="h1" sx={{ 
          fontWeight: 700,
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -8,
            left: 0,
            width: 40,
            height: 3,
            borderRadius: 2,
            backgroundColor: 'primary.main',
          }
        }}>
          Избранные статьи
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {favorites.map((article) => (
          <Grid item xs={12} sm={6} md={4} key={article.id}>
            <Card 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 2,
                overflow: 'hidden',
                border: `1px solid ${alpha(theme.palette.divider, 0.25)}`,
                background: mode === 'light'
                  ? alpha(theme.palette.background.paper, 0.98)
                  : alpha(theme.palette.background.paper, 0.90),
                boxShadow: 'none',
                transition: 'all 0.28s cubic-bezier(.23,1.02,.64,.98)',
                position: 'relative',
                p: 0,
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 4,
                  background: theme.palette.secondary.main,
                  opacity: 0.7,
                  borderTopLeftRadius: 6,
                  borderBottomLeftRadius: 6,
                  zIndex: 1,
                },
                '&:hover': {
                  transform: 'translateY(-7px) scale(1.025)',
                  boxShadow: mode === 'light' 
                    ? '0 8px 32px rgba(0,0,0,0.13)' 
                    : '0 8px 32px rgba(0,0,0,0.33)',
                  borderColor: theme.palette.secondary.main,
                  outline: `2px solid ${theme.palette.secondary.main}`,
                  outlineOffset: '2px',
                },
              }}
            >
              <CardActionArea 
                onClick={() => navigate(`/article/${article.categoryId}/${article.subcategoryId}/${article.id}`)}
                sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', zIndex: 2 }}
              >
                <CardContent sx={{ flex: 1, width: '100%', p: 2, pt: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 1.5 }}>
                    <Box sx={{ display: 'flex', gap: 0.7 }}>
                      <Chip 
                        size="small"
                        label="Закладка"
                        icon={<BookmarkIcon style={{ fontSize: '0.9rem' }} />}
                        sx={{ 
                          backgroundColor: alpha(theme.palette.secondary.main, 0.12),
                          color: theme.palette.secondary.main,
                          fontWeight: 500,
                          fontSize: '0.8rem',
                          height: 24,
                          borderRadius: 1.5,
                        }}
                      />
                    </Box>
                    <IconButton
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        removeFromFavorites(article.id);
                      }}
                      sx={{
                        color: 'error.main',
                        p: 0.5,
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.error.main, 0.12),
                        },
                        transform: 'translateY(-2px)',
                      }}
                      size="small"
                    >
                      <FavoriteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  
                  <Typography 
                    variant="h6" 
                    component="h3"
                    sx={{ 
                      fontWeight: 700,
                      lineHeight: 1.2,
                      mb: 1,
                      minHeight: '2.7rem',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontSize: '1.1rem',
                    }}
                  >
                    {article.title}
                  </Typography>
                  
                  <Divider sx={{ my: 1.5, opacity: 0.2 }} />
                  
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    mt: 'auto', 
                    width: '100%', 
                    pt: 1,
                    opacity: 0.7,
                  }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                      ID: {article.id}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" color="primary" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                        Открыть →
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default Favorites; 